import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useCompaniesSelectionStore = defineStore(
  'insights-companies-selection',
  () => {
    const selectedCompanies = ref<Set<number>>(new Set())
    // Flips true once the user explicitly mutates selection (row checkbox or
    // header select-all). Read by autoSelectInitial so late chunks (e.g. the
    // slow WorkflowProgress show-all column re-saving the same chunk_index)
    // don't re-select rows the user just unchecked. Cleared by reset() so
    // each new request re-enables auto-seed.
    const userTouchedSelection = ref(false)

    function reset(): void {
      selectedCompanies.value = new Set()
      userTouchedSelection.value = false
    }

    function markUserTouched(): void {
      userTouchedSelection.value = true
    }

    function add(id: number): void {
      selectedCompanies.value.add(id)
    }

    function remove(id: number): void {
      selectedCompanies.value.delete(id)
    }

    function replaceWith(ids: Set<number>): void {
      selectedCompanies.value = new Set(ids)
    }

    function retainIntersection(filteredIds: Set<number>): void {
      // Build a new Set (reference swap) so Vue watchers observing
      // selectedCompanies are triggered; in-place mutation is not reactive.
      const next = new Set<number>()
      for (const id of selectedCompanies.value) {
        if (filteredIds.has(id)) next.add(id)
      }
      selectedCompanies.value = next
    }

    function addAll(ids: Iterable<number>): void {
      // Reference swap for Vue reactivity — consistent with replaceWith/reset.
      const next = new Set(selectedCompanies.value)
      for (const id of ids) next.add(id)
      selectedCompanies.value = next
    }

    function autoSelectInitial(ids: Iterable<number>): void {
      if (userTouchedSelection.value) return
      addAll(ids)
    }

    return {
      selectedCompanies,
      userTouchedSelection,
      reset,
      add,
      remove,
      replaceWith,
      retainIntersection,
      addAll,
      autoSelectInitial,
      markUserTouched,
    }
  }
)
